/**
 * What the aircraft says about its own landing, for the COP.
 *
 * Pure, so the rule is testable without a render: the field question this
 * answers (2026-08-09, first COP-commanded Mavic 3T flights) is why a commanded
 * LAND brings the aircraft to ~1 m and stops. The reason is DJI's landing
 * protection - the downward vision system judging the surface unsafe - and until
 * now it existed only on the phone. The operator saw a hang with no cause and
 * the documented workaround was to open Pilot 2 and disable obstacle avoidance
 * entirely (#200).
 *
 * Absence is never an answer here. A source that reports no protection state
 * renders nothing at all, rather than a reassuring "clear".
 */

export type LandingStateTone = 'amber' | 'green';

export interface LandingStateView {
  /** Operator-facing headline. Uppercase, always a state, never a number. */
  label: string;
  /** The aircraft's own enum name, shown verbatim beside the headline. */
  reason: string;
  tone: LandingStateTone;
  /** Hover text: what the state means and what the operator can do about it. */
  title: string;
}

/**
 * States that are not worth a chip.
 *
 * `NONE` is the aircraft saying it is not evaluating a surface; `UNKNOWN` is it
 * saying it cannot tell. Neither is a hold, and putting either on a command bar
 * would be permanent furniture the operator learns to stop reading - which is
 * how the one state that matters gets missed.
 */
const QUIET_STATES: ReadonlySet<string> = new Set(['NONE', 'UNKNOWN']);

const HOLD_TITLE =
  'The aircraft stopped short of touchdown: its downward vision system will not '
  + 'accept the surface. CONFIRM LANDING overrides that - only if you can see the '
  + 'ground below the aircraft is clear.';

export function landingStateView(drone: {
  is_flying?: boolean;
  landing_protection?: string;
}): LandingStateView | null {
  const state = drone.landing_protection;
  if (typeof state !== 'string' || state === '' || QUIET_STATES.has(state)) return null;

  // Explicitly on the ground: the protection state belonged to a landing that
  // has finished. Only an explicit `false` suppresses it - absent means the
  // source cannot answer, and suppressing on "unknown" would hide the chip on
  // exactly the aircraft whose hang nobody can explain.
  if (drone.is_flying === false) return null;

  if (state === 'NOT_SAFE_TO_LAND') {
    return { label: 'Holding short', reason: state, tone: 'amber', title: HOLD_TITLE };
  }
  if (state === 'ANALYSIS_FAILED') {
    return {
      label: 'Holding short',
      reason: state,
      tone: 'amber',
      // A different problem from NOT_SAFE_TO_LAND and the operator's decision
      // differs with it: the vision system failed to make a judgement rather
      // than making a negative one.
      title: 'The aircraft stopped short of touchdown: its downward vision system could '
        + 'not evaluate the surface at all. CONFIRM LANDING overrides that - only if you '
        + 'can see the ground below the aircraft is clear.',
    };
  }
  if (state === 'ANALYZING') {
    return {
      label: 'Checking surface',
      reason: state,
      tone: 'amber',
      title: 'The aircraft is evaluating the surface below it. This resolves on its own.',
    };
  }
  if (state === 'SAFE_TO_LAND') {
    return {
      label: 'Surface accepted',
      reason: state,
      tone: 'green',
      title: 'The aircraft judges the surface below it safe to land on.',
    };
  }
  // A state name this build has never seen. Show it rather than swallow it - the
  // whole point of carrying the enum NAME across the wire is that an aircraft
  // can tell us something we did not anticipate.
  return {
    label: 'Landing protection',
    reason: state,
    tone: 'amber',
    title: 'The aircraft reported a landing-protection state this build does not recognise.',
  };
}
